import styled, { createGlobalStyle } from "styled-components";
import { BRAND, TINTS } from "../../helper/brand";

// 🎨 Éléments communs du planning (créneaux de la fiche du prof, réservation, « Passer en ligne ? »,
// conseil d'utiliser un ordinateur) : pastilles de créneaux, boutons, étiquettes, encadrés. Couleurs de
// la charte (helper/brand.js), mode sombre, lisible sur téléphone (zones de clic d'au moins 40 px,
// boutons pleine largeur sous 576 px).
// Les fenêtres (Modal) sont hors de la page : leur contenu est aussi enveloppé dans PlanningUi.
// ☀️ Thème clair (tour 43) et sombre en gris chauds (tour 49) : variables du site (« --nd-… »), avec une
// valeur de repli.
const dark = (p) => p.mode === "dark";
const nd = (name, fallback) => `var(--nd-${name}, ${fallback})`;
const text = (p) => (dark(p) ? nd("texte", "#ffffff") : BRAND.black);
const muted = (p) => (dark(p) ? nd("texte-doux", TINTS.mutedDark) : nd("texte-doux", TINTS.muted));
const line = (p) => (dark(p) ? nd("trait", TINTS.lineDark) : nd("trait", TINTS.line));
const blueSoft = nd("bleu-doux", TINTS.blueSoft);

export const PlanningUi = styled.div`
  color: ${text};

  .acd-card-title { display: flex; align-items: center; gap: 8px; font-weight: 600; font-size: 1.1rem; margin: 0 0 4px; color: ${text}; }
  .acd-card-title .acd-ico { color: ${(p) => (dark(p) ? BRAND.lightBlue : BRAND.blue)}; flex-shrink: 0; }
  .acd-muted { color: ${muted}; font-size: 0.875rem; }
  /* 💶 « Découvrir Premium » sous le prix (tour 57) : bleu en clair, bleu clair en sombre. En sombre, la règle
     ancienne « body.rs-theme-dark .modal-content * » met tout le texte des fenêtres en blanc (!important) : la
     classe doublée et !important passent devant (trouvé par les captures) */
  .acd-lien-note.acd-lien-note { color: ${(p) => (dark(p) ? BRAND.lightBlue : BRAND.blue)} !important; font-weight: 600; }
  .acd-label { font-size: 0.85rem; font-weight: 600; color: ${text}; margin-bottom: 4px; }

  /* Créneaux : pastilles bien visibles, en grille (4 par ligne sur téléphone) */
  .acd-slots { display: grid; grid-template-columns: repeat(auto-fill, minmax(76px, 1fr)); gap: 8px; }
  .acd-pill {
    min-height: 42px;
    border-radius: 999px;
    border: 1.5px solid ${BRAND.orange};
    background: ${(p) => (dark(p) ? nd("fond", BRAND.black) : "#ffffff")};
    color: ${text};
    font-weight: 600;
    font-size: 0.95rem;
    transition: background 0.15s ease, color 0.15s ease;
  }
  .acd-pill:hover, .acd-pill:focus-visible { background: ${BRAND.orange}; color: #ffffff; outline: none; }

  /* Choix (durée…) : pastilles, l'élément choisi en orange */
  .acd-segment { display: flex; flex-wrap: wrap; gap: 8px; }
  .acd-seg {
    min-height: 38px;
    border-radius: 999px;
    border: 1.5px solid ${line};
    background: transparent;
    color: ${text};
    font-weight: 600;
    padding: 6px 16px;
  }
  .acd-seg[aria-pressed="true"] { background: ${BRAND.orange}; border-color: ${BRAND.orange}; color: #ffffff; }
  /* Nouveau design (tour 40) : créneaux en bleu (bord, puis fond bleu et texte blanc au survol) et
     choix fait en bleu (durées…), l'orange reste à l'action principale de l'écran */
  body.nd & .acd-pill { border-color: var(--nd-lien); }
  body.nd & .acd-pill:hover,
  body.nd & .acd-pill:focus-visible { background: var(--nd-secondaire); color: var(--nd-secondaire-texte); }
  body.nd & .acd-seg[aria-pressed="true"] { background: var(--nd-secondaire); border-color: var(--nd-secondaire); color: var(--nd-secondaire-texte); }
  /* Bouton orange : texte noir, comme les autres boutons orange du nouveau design (blanc sur
     orange : contraste 2,3, tour 49) */
  body.nd & .acd-btn-primary,
  body.nd & .acd-btn-primary:hover,
  body.nd & .acd-btn-primary:disabled { color: var(--nd-action-texte); }

  /* Boutons */
  .acd-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    min-height: 44px; padding: 10px 18px; border-radius: 12px; border: 0;
    font-weight: 600; font-size: 0.95rem; line-height: 1.2; text-decoration: none;
  }
  .acd-btn-primary { background: ${BRAND.orange}; color: #ffffff; }
  .acd-btn-primary:hover { background: ${TINTS.orangeHover}; color: #ffffff; }
  .acd-btn-primary:disabled { background: ${TINTS.orangeDisabled}; color: #ffffff; cursor: not-allowed; }
  .acd-btn-blue { background: ${BRAND.blue}; color: #ffffff; }
  .acd-btn-blue:hover { background: #055c69; color: #ffffff; }
  .acd-btn-ghost { background: transparent; color: ${text}; border: 1.5px solid ${line}; }
  .acd-btn-ghost:hover { border-color: ${muted}; }
  .acd-btn-sm { min-height: 36px; padding: 6px 12px; font-size: 0.85rem; border-radius: 10px; }
  .acd-btn:disabled { opacity: 0.6; }
  .acd-icon-btn {
    width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    border: 1.5px solid ${line}; background: transparent; color: ${text};
  }
  .acd-icon-btn:disabled { opacity: 0.35; }
  .acd-actions { display: flex; flex-wrap: wrap; gap: 8px; justify-content: flex-end; }

  /* Étiquettes et encadrés */
  .acd-chip { display: inline-flex; align-items: center; gap: 6px; border-radius: 999px; padding: 3px 10px; font-size: 0.8rem; font-weight: 600; }
  .acd-chip-orange { background: ${TINTS.orangeSoft}; color: ${(p) => (dark(p) ? "#f9b24f" : nd("orange-texte", "#a85f00"))}; }
  .acd-chip-green { background: rgba(25, 135, 84, 0.12); color: ${(p) => (dark(p) ? "#6fd3a1" : "#146c43")}; }
  .acd-note { display: flex; gap: 10px; align-items: flex-start; border-radius: 12px; padding: 12px 14px; font-size: 0.9rem; margin-bottom: 12px; }
  .acd-note > svg { flex-shrink: 0; margin-top: 2px; }
  .acd-note-info { background: ${(p) => (dark(p) ? "rgba(165, 218, 237, 0.12)" : blueSoft)}; }
  .acd-note-warn { background: ${TINTS.orangeSoft}; }
  .acd-note a, .acd-note .btn-link, .acd-recap a, .acd-recap .btn-link { color: ${(p) => (dark(p) ? BRAND.lightBlue : BRAND.blue)}; font-weight: 600; }

  /* Récapitulatif (réservation) : une ligne par information */
  .acd-recap { list-style: none; padding: 0; margin: 0 0 12px; }
  .acd-recap li { display: flex; gap: 12px; align-items: flex-start; padding: 10px 0; border-bottom: 1px solid ${line}; }
  .acd-recap li:last-child { border-bottom: 0; }
  .acd-recap .acd-ico { color: ${(p) => (dark(p) ? BRAND.lightBlue : BRAND.blue)}; flex-shrink: 0; margin-top: 2px; }
  .acd-recap .acd-recap-label { font-size: 0.8rem; color: ${muted}; }
  .acd-recap .acd-recap-value { font-weight: 600; }

  @media (max-width: 575.98px) {
    .acd-actions { flex-direction: column-reverse; }
    .acd-actions .acd-btn { width: 100%; }
  }
`;

// Fenêtres du planning : coins arrondis, sans bordure
export const PlanningModalStyle = createGlobalStyle`
  .acd-modal .modal-content { border: 0; border-radius: 18px; overflow: hidden; }
  .acd-modal .modal-body { padding: 0; }
  /* 🌙 Thème sombre (tour 58) : la règle ancienne « body.rs-theme-dark .modal-content * » met tout le texte des
     fenêtres en blanc (!important), liens compris. Dans les fenêtres du planning, les liens reprennent la
     couleur des liens du site en sombre (bleu clair), boutons à part. Rien ne change en clair */
  body.rs-theme-dark .acd-modal .modal-content a:not(.acd-btn):not(.btn):not(.nd-btn),
  body.rs-theme-dark .acd-modal .modal-content .btn-link { color: ${BRAND.lightBlue} !important; }
`;
